import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { lugar } from '@/lib/lugar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  Shield, CheckCircle, AlertTriangle,
  Loader2, Lock, Zap, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERIFICATION_TYPES = [
  {
    id: 'selleverify',
    label: 'SelleVerify',
    description: 'AI-powered identity & address verification',
    icon: Shield,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    price: 1.99
  },
];

// Fields we never expose to the merchant — hides which fraud tools we use.
const HIDDEN_DETAIL_KEYS = new Set([
  'fraud_services_used', 'services_used', 'services_count',
  'detailed_results', 'raw_results', 'ipqs', 'maxmind', 'kount',
  'provider', 'providers',
]);

const VerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(searchParams.get('shipment') || '');
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await axios.get(`${API}/shipments`, {
          headers: getAuthHeaders()
        });
        setShipments(response.data);
      } catch (error) {
        console.error('Failed to fetch shipments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [getAuthHeaders]);

  useEffect(() => {
    if (selectedShipment) {
      fetchVerifications();
    }
  }, [selectedShipment]);

  const fetchVerifications = async () => {
    try {
      const response = await axios.get(`${API}/verifications/${selectedShipment}`, {
        headers: getAuthHeaders()
      });
      // Only show currently supported verification types (hide legacy ledger/item-scoring history).
      const supported = new Set(VERIFICATION_TYPES.map(v => v.id));
      setVerifications((response.data || []).filter(v => supported.has(v.verification_type)));
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
    }
  };

  const runVerification = async (type) => {
    if (!selectedShipment) {
      toast.error('Please select a shipment first');
      return;
    }

    setVerifying(type);
    try {
      const response = await axios.post(`${API}/verify`, {
        shipment_id: selectedShipment,
        verification_type: type
      }, {
        headers: getAuthHeaders()
      });

      // Process transaction for the verification
      await axios.post(`${API}/transactions`, {
        shipment_id: selectedShipment,
        services: [type]
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Verification completed');
      fetchVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const getVerificationResult = (type) => {
    return verifications.find(v => v.verification_type === type);
  };

  const getScoreColor = (score) => {
    if (score >= 0.7) return 'score-high';
    if (score >= 0.4) return 'score-medium';
    return 'score-low';
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="verification-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Verification Center</h1>
        <p className="text-slate-500 mt-1">Run verification checks on your shipments</p>
      </div>

      {/* Shipment Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Select Shipment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedShipment} onValueChange={setSelectedShipment}>
            <SelectTrigger className="w-full md:w-96" data-testid="shipment-selector">
              <SelectValue placeholder="Select a shipment to verify" />
            </SelectTrigger>
            <SelectContent>
              {shipments.map((shipment) => (
                <SelectItem key={shipment.id} value={shipment.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{shipment.id.slice(0, 8)}...</span>
                    <span className="text-slate-500">
                      → {lugar(shipment.ship_to)}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Verification Options */}
      <div className="grid md:grid-cols-1 gap-6 max-w-2xl">
        {VERIFICATION_TYPES.map((vType) => {
          const result = getVerificationResult(vType.id);
          const isVerifying = verifying === vType.id;

          return (
            <Card 
              key={vType.id} 
              className={`transition-all ${result ? 'border-emerald-200 dark:border-emerald-800' : ''}`}
              data-testid={`verification-card-${vType.id}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className={`w-12 h-12 ${vType.bgColor} rounded-xl flex items-center justify-center`}>
                    <vType.icon className={`w-6 h-6 ${vType.color}`} />
                  </div>
                  {result && (
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                  )}
                </div>
                <CardTitle className="text-lg">{vType.label}</CardTitle>
                <CardDescription>{vType.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-4">
                    {result.score !== null && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-500">Score</span>
                          <span className="font-semibold tabular-nums">
                            {(result.score * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="score-indicator">
                          <div 
                            className={`score-fill ${getScoreColor(result.score)}`}
                            style={{ width: `${result.score * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {result.ledger_hash && (
                      <div>
                        <span className="text-sm text-slate-500 block mb-2">Ledger Hash</span>
                        <div className="ledger-hash">
                          {result.ledger_hash}
                        </div>
                      </div>
                    )}

                    {result.details && (
                      <div className="space-y-2">
                        {Object.entries(result.details)
                          .filter(([key]) => !HIDDEN_DETAIL_KEYS.has(key.toLowerCase()))
                          .map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 capitalize">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="font-medium">
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                )
                              ) : typeof value === 'object' && value !== null ? (
                                <span className="text-xs text-slate-400">—</span>
                              ) : (
                                String(value)
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-slate-400">
                      Verified {new Date(result.created_at).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Cost</span>
                      <span className="font-semibold">${vType.price.toFixed(2)}</span>
                    </div>
                    <Button 
                      className="w-full btn-press"
                      onClick={() => runVerification(vType.id)}
                      disabled={!selectedShipment || isVerifying}
                      data-testid={`verify-btn-${vType.id}`}
                    >
                      {isVerifying ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Run Verification
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Verification History */}
      {verifications.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Verification History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {verifications.map((v) => (
                    <tr key={v.id}>
                      <td className="capitalize">{v.verification_type.replace(/_/g, ' ')}</td>
                      <td>
                        <span className={`status-badge status-${v.status}`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="tabular-nums">
                        {v.score !== null ? `${(v.score * 100).toFixed(0)}%` : '—'}
                      </td>
                      <td className="text-slate-500">
                        {new Date(v.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!selectedShipment && (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium mb-2">Select a Shipment</h3>
            <p className="text-slate-500 max-w-sm mx-auto">
              Choose a shipment from the dropdown above to run verification checks.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VerificationPage;
